import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Constants } from '@praktio/common';

import './CustomNav.scss';
import { RootState } from '../../ducks/configureStore';
import { getNext, update } from '../../ducks/slidesLitmosReducer';

const { NavStatuses } = Constants;

const renderProgressCircle = (navItem: NavItem) => {
  if (!navItem.parentId) {
    return null;
  }

  let circleClass = '';
  let semiCircle = null;
  let text = null;
  switch (navItem.progress) {
    case NavStatuses.InProgress:
      circleClass = 'in-progress';
      semiCircle = <path d="M 50 0 A 45 45 0 1 0 50 100 Z" className="semicircle"></path>;
      break;
    case NavStatuses.Complete:
      circleClass = 'completed';
      text = (
        <text x="50%" y="50%" textAnchor="middle" className="material-icons completed-check">
          check
        </text>
      );
      break;
    default:
      circleClass = 'no-progress';
      break;
  }

  return (
    <div className="progress-circle">
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="50" className={circleClass}></circle>
        {semiCircle}
        {text}
      </svg>
    </div>
  );
};

const renderNavItems = (navItems: NavItem[], onNavItemClick: (id: string) => void) => {
  const elements = navItems.map((n) => {
    const indexes = [n.order + 1];
    const isBottom = !n.children || n.children.length === 0;
    let parentId = n.parentId;
    if (parentId) {
      while (parentId) {
        // eslint-disable-next-line no-loop-func
        const parent = navItems.find((p) => p._id === parentId);
        if (parent) {
          indexes.unshift(parent.order + 1);
          parentId = parent.parentId;
        }
      }
    } else {
      indexes.push(0);
    }

    return (
      <div
        key={n._id}
        onClick={n.slides && n.slides.length ? () => onNavItemClick(n._id) : undefined}
        className={classNames('custom-nav-item', {
          parent: n.children && n.children.length,
        })}
      >
        <div className="column progress-column">{renderProgressCircle(n)}</div>
        <div className="column index-column">{indexes.join('.')}</div>
        <div className="column title-column">
          <span className="material-icons-outlined pr-5">
            {isBottom ? 'text_snippet' : 'folder'}
          </span>
          {n.title}
        </div>
        <div className="column status-column">
          {n.progress === NavStatuses.Complete
            ? 'Completed'
            : n.progress === NavStatuses.InProgress
            ? 'In Progress'
            : 'Not Started'}
        </div>
      </div>
    );
  });

  return elements;
};

const CustomNav = () => {
  const dispatch = useDispatch();
  const { currentSlide } = useSelector((state: RootState) => {
    const { data: module, currentSlide } = state.slidesLitmos;
    return { module, currentSlide };
  });

  const onNavItemClick = (navItemId: string) => {
    dispatch(
      update({
        navItemId,
      }),
    );
    dispatch(getNext());
  };

  const { customNav, allComplete } = (currentSlide || {}) as Slide;
  const flatNav: NavItem[] = [];
  let itemsCount = 0;
  let itemsReviewed = 0;
  const getNavItem = (item: NavItem) => {
    flatNav.push(item);
    if (item.children) {
      item.children.forEach((c) => getNavItem(c));
    }

    if (!item.children || !item.children.length) {
      itemsCount += 1;
      if (item.progress === NavStatuses.Complete) {
        itemsReviewed += 1;
      }
    }
  };

  if (customNav && customNav.navItems) {
    customNav.navItems.forEach((n) => getNavItem(n));
  }

  const navInstructions = allComplete
    ? `Congratulations! You've completed this module.`
    : 'Click on a document to continue';
  const progressPercent = itemsCount ? (itemsReviewed || 0) / itemsCount : 0;
  return (
    <div className="slide-content slide-content-custom-nav">
      <div className="slide-panel slide-custom-nav">
        <div className="custom-nav-instructions">{navInstructions}</div>
        <div className="custom-nav-container">
          <div className="custom-nav-title">{customNav?.title}</div>
          <div className="custom-nav-table">
            <div className="custom-nav-header">
              <div className="column progress-column"></div>
              <div className="column index-column">Index</div>
              <div className="column title-column">Title</div>
              <div className="column status-column">Status</div>
            </div>
            <div className="custom-nav-data">{renderNavItems(flatNav, onNavItemClick)}</div>
            <div className="custom-nav-footer" />
          </div>
          <div className="custom-nav-progress">
            <div className="progress-percent">
              <svg viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  strokeWidth="10"
                  strokeDasharray={2 * Math.PI * 45}
                  strokeDashoffset={2 * Math.PI * 45 * (1 - progressPercent)}
                  transform="rotate(-90 50 50)"
                  className="progress-ring"
                ></circle>
                <circle className="inner-circle" cx="50" cy="50" r="40"></circle>
                <text x="50%" y="50%" textAnchor="middle" className="percent-value">
                  {Math.round(progressPercent * 100)}%
                </text>
              </svg>
            </div>
            <div className="progress-docs">
              <div>Total Documents: {itemsCount}</div>
              <div>Total Documents Reviewed: {itemsReviewed}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomNav;
